// GitHub-flavor callout blockquote rewriter.
//
// Rewrites `> [!type]\n> body` into <aside class="callout callout-{type}">
// with a synthetic <div class="callout-title">{Type}</div> header.
//
// Recognised types: note, tip, important, warning, caution.
//
// Algorithm: walk markdown-it's flat token array. For each blockquote_open
// whose first inline child starts with `[!type]`, rewrite the open/close
// tags to `aside`, attach `class="callout callout-<type>"`, prepend a
// title token, and strip the marker from the inline content.

import type MarkdownIt from 'markdown-it';

const TYPES = new Set(['note', 'tip', 'important', 'warning', 'caution']);
const MARKER_RE = /^\[!([A-Za-z]+)\]\s*\n?/;

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function calloutsPlugin(md: MarkdownIt): void {
  md.core.ruler.after('block', 'gh_callouts', (state) => {
    const tokens = state.tokens;
    for (let i = 0; i < tokens.length; i++) {
      const tk = tokens[i]!;
      if (tk.type !== 'blockquote_open') continue;

      const inline =
        tokens[i + 1]?.type === 'paragraph_open' && tokens[i + 2]?.type === 'inline'
          ? tokens[i + 2]!
          : null;
      if (!inline) continue;

      const m = MARKER_RE.exec(inline.content);
      if (!m) continue;
      const type = m[1]!.toLowerCase();
      if (!TYPES.has(type)) continue;

      inline.content = inline.content.slice(m[0].length).replace(/^\s+/, '');
      if (Array.isArray(inline.children) && inline.children.length) {
        let dropTo = 0;
        let acc = '';
        for (; dropTo < inline.children.length; dropTo++) {
          const ch = inline.children[dropTo]!;
          acc += ch.content || '';
          if (acc.length >= m[0].length) {
            dropTo++;
            break;
          }
        }
        inline.children.splice(0, dropTo);
        if (inline.children[0] && inline.children[0].type === 'softbreak') {
          inline.children.shift();
        }
      }

      tk.tag = 'aside';
      tk.attrJoin('class', `callout callout-${type}`);
      let depth = 1;
      let j = i + 1;
      for (; j < tokens.length; j++) {
        if (tokens[j]!.type === 'blockquote_open') depth++;
        else if (tokens[j]!.type === 'blockquote_close') {
          depth--;
          if (depth === 0) break;
        }
      }
      if (j < tokens.length) tokens[j]!.tag = 'aside';

      const titleOpen = new state.Token('callout_title_open', 'div', 1);
      titleOpen.attrJoin('class', 'callout-title');
      const titleInline = new state.Token('inline', '', 0);
      titleInline.content = cap(type);
      titleInline.children = [];
      const titleClose = new state.Token('callout_title_close', 'div', -1);
      tokens.splice(i + 1, 0, titleOpen, titleInline, titleClose);
      i += 3;
    }
    return true;
  });

  md.renderer.rules.callout_title_open = () => '<div class="callout-title">';
  md.renderer.rules.callout_title_close = () => '</div>';
}
